import uniq from "lodash/uniq";

import type { FilterableColumn } from "src/types/visual";

type SegmentWithId = {
  id: string | number;
};

type RelationshipWithToModel = {
  to_model: SegmentWithId;
};

type HydratableParentModel = SegmentWithId & {
  filterable_audience_columns?: FilterableColumn[];
  relationships?: Array<
    RelationshipWithToModel & {
      to_model: SegmentWithId & {
        filterable_audience_columns?: FilterableColumn[];
      };
    }
  >;
};

export type RelationshipWithFilterableColumns<
  R extends RelationshipWithToModel,
> = Omit<R, "to_model"> & {
  to_model: R["to_model"] & {
    filterable_audience_columns: FilterableColumn[];
  };
};

export type ParentModelWithFilterableColumns<T extends HydratableParentModel> =
  Omit<T, "filterable_audience_columns" | "relationships"> & {
    filterable_audience_columns: FilterableColumn[];
    relationships: T["relationships"] extends infer Rels
      ? Rels extends Array<infer R extends RelationshipWithToModel>
        ? Array<RelationshipWithFilterableColumns<R>>
        : Rels
      : never;
  };

export function collectFilterableSegmentIds(
  parentModelId: string | number | undefined | null,
  relationships?: ReadonlyArray<RelationshipWithToModel> | null,
): string[] {
  if (parentModelId == null || parentModelId === "") {
    return [];
  }

  return uniq([
    String(parentModelId),
    ...(relationships ?? []).map((relationship) =>
      String(relationship.to_model.id),
    ),
  ]);
}

export function collectFilterableSegmentIdsNeedingFetch(
  parent: HydratableParentModel | null | undefined,
): string[] {
  if (parent?.id == null || parent.id === "") {
    return [];
  }

  const segmentIds: string[] = [];

  if (parent.filterable_audience_columns == null) {
    segmentIds.push(String(parent.id));
  }

  for (const relationship of parent.relationships ?? []) {
    if (relationship.to_model.filterable_audience_columns == null) {
      segmentIds.push(String(relationship.to_model.id));
    }
  }

  return uniq(segmentIds);
}

export function getFilterableColumnsForSegment(
  columnsByModelId: Record<string, FilterableColumn[]>,
  segmentId: string | number | undefined | null,
): FilterableColumn[] {
  if (segmentId == null) {
    return [];
  }

  return columnsByModelId[String(segmentId)] ?? [];
}

function resolveFilterableColumnsForSegment({
  existingColumns,
  columnsByModelId,
  segmentId,
}: {
  existingColumns?: FilterableColumn[] | null;
  columnsByModelId: Record<string, FilterableColumn[]>;
  segmentId: string | number;
}): FilterableColumn[] {
  return (
    existingColumns ??
    getFilterableColumnsForSegment(columnsByModelId, segmentId)
  );
}

function hydrateRelationshipWithFilterableColumns<
  R extends RelationshipWithToModel & {
    to_model: SegmentWithId & {
      filterable_audience_columns?: FilterableColumn[];
    };
  },
>(
  relationship: R,
  columnsByModelId: Record<string, FilterableColumn[]>,
): RelationshipWithFilterableColumns<R> {
  return {
    ...relationship,
    to_model: {
      ...relationship.to_model,
      filterable_audience_columns: resolveFilterableColumnsForSegment({
        existingColumns: relationship.to_model.filterable_audience_columns,
        columnsByModelId,
        segmentId: relationship.to_model.id,
      }),
    },
  };
}

export function hydrateParentModelWithFilterableColumns<
  T extends HydratableParentModel,
>(
  parent: T,
  columnsByModelId: Record<string, FilterableColumn[]>,
): ParentModelWithFilterableColumns<T> {
  return {
    ...parent,
    filterable_audience_columns: resolveFilterableColumnsForSegment({
      existingColumns: parent.filterable_audience_columns,
      columnsByModelId,
      segmentId: parent.id,
    }),
    // TypeScript cannot prove the mapped array matches ParentModelWithFilterableColumns<T>["relationships"]
    // even though hydrateRelationshipWithFilterableColumns preserves each relationship's shape.
    relationships: parent.relationships?.map((relationship) =>
      hydrateRelationshipWithFilterableColumns(relationship, columnsByModelId),
    ) as ParentModelWithFilterableColumns<T>["relationships"],
  };
}
